import React from 'react';
import { IStore } from 'Store/.';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { Dispatch, bindActionCreators } from 'redux';
import { MenuView } from 'Store/User/reducer';
import FormAccount from 'Components/FormAccount/FormAccount';

import { 
  getLoginInput, 
  getLoggedInUser, 
  getMenuView,
} from 'Store/User/selectors';

import { 
  createAccount, 
  updateAccount, 
  updatePassword,
  fetchCountryList,
} from 'Store/User/thunks';

interface IPropsInternal {
  menuView: MenuView;
  loggedInUser: Julius.Website.CustomerInfoViewModel;
  loginInput: Julius.Website.CustomerLoginViewModel;
  createAccount: (userInfo: Julius.Website.CreateCustomerViewModel) => void;
  updateAccount: (userInfo: Julius.Website.CreateCustomerViewModel) => void;
  updatePassword: (passwordInfo: Julius.Website.ChangePasswordViewModel) => void;
}

type Props = IPropsInternal;

interface IState {
  isTermsWarning: boolean;
  isPasswordWarning: boolean;
  passwordInput: Julius.Website.ChangePasswordViewModel;
  accountInput: Julius.Website.CreateCustomerViewModel;
}

class FormAccountContainer extends React.PureComponent<Props, IState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      passwordInput: this.getInitialPasswordInput(),
      accountInput: this.getInitialAccountInput(this.props.loggedInUser),
      isTermsWarning: false,
      isPasswordWarning: false,
    };
  }

  getInitialPasswordInput = () => {
    return {
      oldPassword: '',
      password: '',
      password2: '',
    };
  }

  getInitialAccountInput = (loggedInUser: Julius.Website.CustomerInfoViewModel) => {
    if (loggedInUser) {
      return {
        firstName: loggedInUser.firstName,
        lastName: loggedInUser.lastName,
        email: loggedInUser.email,
        phone: loggedInUser.phone,
        street: loggedInUser.street,
        zipcode: loggedInUser.zipcode,
        city: loggedInUser.city,
        country: loggedInUser.country,
        company: loggedInUser.company,
        customerID: loggedInUser.customerID,
        isEmailsAllowed: loggedInUser.isEmailsAllowed,
        isUserTermsApproved: true,
        password: '',
        password2: '',
      };
    }
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      street: '',
      zipcode: '',
      city: '',
      country: '',
      company: '',
      customerID: -1,
      isEmailsAllowed: false,
      isUserTermsApproved: false,
      password: '',
      password2: '',
    };
  };

  componentDidMount() {
    const { menuView } = this.props;

    if (menuView === MenuView.CREATE_ACCOUNT) {
      const { email, password } = this.props.loginInput;
      this.setState({ accountInput: { ...this.state.accountInput, email, password } });
    }
    if (menuView === MenuView.UPDATE_ACCOUNT) {
      this.setState({ accountInput: { ...this.state.accountInput, isUserTermsApproved: true } });
    }
  }

  onChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { menuView } = this.props;

    if (menuView === MenuView.UPDATE_PASSWORD) {
      this.setState({
        isPasswordWarning: false,
        passwordInput: {
          ...this.state.passwordInput,
          [e.currentTarget.name]: e.currentTarget.value,
        },
      });
    } else {
      this.setState({ 
        isPasswordWarning: false,
        accountInput: { 
          ...this.state.accountInput, 
          [e.currentTarget.name]: e.currentTarget.value,
        },
      });
    }
  }

  onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { menuView } = this.props;

    if (!this.isPasswordsMatching()) {
      this.setState({ isPasswordWarning: true });
      return;
    }
    if (menuView === MenuView.UPDATE_PASSWORD) {
      this.updatePassword();
      return;
    }
    if (!this.isTermsApproved()) {
      this.setState({ isTermsWarning: true });
      return;
    }
    if (menuView === MenuView.CREATE_ACCOUNT) {
      this.createAccount();
      return;
    }
    if (menuView === MenuView.UPDATE_ACCOUNT) {
      this.updateAccount();
    }
  }

  onUserTermsChange = (isApproved: boolean) => {
    const isTermsWarning = isApproved ? false : this.state.isTermsWarning;
    this.setState({ 
      accountInput: { 
        ...this.state.accountInput, 
        isUserTermsApproved: isApproved,
      }, 
      isTermsWarning,
    });
  }

  onEmailTermsChange = (isApproved: boolean) => {
    this.setState({ 
      accountInput: { 
        ...this.state.accountInput, 
        isEmailsAllowed: isApproved,
      },
    });
  }

  onCountryChange = (value: string) => {
    this.setState({
      accountInput: { 
        ...this.state.accountInput, 
        country: value,
      },
    });
  }

  isTermsApproved = () => {
    return this.state.accountInput.isUserTermsApproved;
  }

  isPasswordsMatching = () => {
    const { menuView } = this.props;
    const { password, password2 } = menuView === MenuView.CREATE_ACCOUNT ?
      this.state.accountInput : this.state.passwordInput;
    return password === password2;
  }

  updatePassword = () => {
    const { passwordInput } = this.state;
    this.props.updatePassword(passwordInput);
  }

  createAccount = () => {
    const { accountInput } = this.state;
    this.props.createAccount(accountInput);
  }

  updateAccount = () => {
    const { accountInput } = this.state;
    this.props.updateAccount(accountInput);
  }

  render() {
    return (
      <FormAccount
        menuView={ this.props.menuView }
        isTermsWarning={ this.state.isTermsWarning }
        isPasswordWarning={ this.state.isPasswordWarning }
        accountInput={ this.state.accountInput }
        passwordInput={ this.state.passwordInput }
        onUserTermsChange={ this.onUserTermsChange }
        onEmailTermsChange={ this.onEmailTermsChange }
        onCountryChange={ this.onCountryChange }
        onChange={ this.onChange }
        onSubmit={ this.onSubmit }
      />
    );
  }
}

const mapStateToProps = (state: IStore) => {
  return {
    menuView: getMenuView(state),
    loggedInUser: getLoggedInUser(state),
    loginInput: getLoginInput(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return bindActionCreators({
    createAccount,
    updateAccount,
    updatePassword,
    fetchCountryList,
  }, dispatch);
};

export default compose<{}, {}>(
  withRouter,
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps),
)(FormAccountContainer);


// WEBPACK FOOTER //
// ./src/components/FormAccount/FormAccountContainer.tsx